import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@servwell/api/router';
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  type TicketCategory,
  type TicketPriority,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Notice } from '../ui/Badge';
import { useTapOnce } from '../ui/useTapOnce';
import { refreshUntilShown } from '../ui/QueryStates';
import { CreateFooter } from '../create/CreateFooter';
import { Field, field } from '../create/Field';
import { sendOnce } from '../tickets/sendOnce';
import { categoryLabel, newTicketMissing, priorityLabel } from '../tickets/ticketView';

type Outputs = inferRouterOutputs<AppRouter>;

/**
 * Who the ticket would be for, as `alert.get` answers it (`ticketTarget`): the customer, and the
 * job when the alert is about one. Null there means the panel does not offer the action.
 */
export type OpenTicketTarget = NonNullable<Outputs['alert']['get']['ticketTarget']>;

/** What `alert.openTicket` answers: the ticket it opened. */
export type OpenedTicket = Outputs['alert']['openTicket'];

/**
 * "Open a ticket" from an alert (Phase 7 spec §4): a small form, title, category and priority,
 * with the customer (and job) shown as the server read them off the alert's subject. They are
 * shown, never sent: the server decides who the ticket is for, so the form cannot name another
 * customer. The ticket is created in the same save as the alert's `ticket_opened`.
 *
 * 🔌 MOUNT POINT (ticket 02, the alert panel): render it in the panel when
 * `alert.ticketTarget` is non-null (the server's one rule, `openTicketRefusal`: never for stock,
 * ticket, resolved or Testing alerts, or once a ticket was opened), e.g. behind an "Open a
 * ticket" button, and link `alert.openedTicket` (`/tickets?ticket=<id>`) once there is one.
 * `onOpened` gets the new ticket; every `alert.*` read is refetched on success, so the panel's
 * link appears by itself.
 */
export function OpenTicketForm({
  alertId,
  target,
  onCancel,
  onOpened,
}: {
  alertId: string;
  target: OpenTicketTarget;
  onCancel: () => void;
  onOpened?: (ticket: OpenedTicket) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const reasonId = useId();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');

  const open = useMutation(
    trpc.alert.openTicket.mutationOptions({
      // The alert is read again on success AND on a refusal ("already opened from this
      // alert"), awaited before the tap lock lets go (TanStack awaits these before
      // `onSettled`): the panel then draws the ticket's link, or drops this stale form.
      onSuccess: async (ticket) => {
        await refreshUntilShown(qc, trpc.alert.pathFilter());
        onOpened?.(ticket);
      },
      onError: () => refreshUntilShown(qc, trpc.alert.pathFilter()),
      onSettled: () => lock.release(),
    }),
  );

  const missing = newTicketMissing({ customerId: target.customer.id, title, category });

  return (
    <div
      role="group"
      aria-label="Open a ticket"
      // A size container: the form sits in the panel, so its two columns follow the PANEL's
      // width, never the window's (LESSONS: fixed-width panels need `@container`).
      className="@container space-y-3 rounded-[var(--radius)] border border-border p-3"
    >
      {open.error ? (
        <Notice tone="danger" alert>
          {open.error.message}
        </Notice>
      ) : null}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Customer</dt>
        <dd className="min-w-0 break-words">{target.customer.name}</dd>
        {target.job ? (
          <>
            <dt className="text-muted-foreground">Job</dt>
            <dd className="min-w-0 break-words">
              #{target.job.number} {target.job.title}
            </dd>
          </>
        ) : null}
      </dl>
      <Field label="Title">
        <input
          className={field}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Call them about the missed visit"
          autoFocus
        />
      </Field>
      <div className="grid gap-3 @sm:grid-cols-2">
        <Field label="Category">
          <select
            className={field}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Choose a category</option>
            {TICKET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <select
            className={field}
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
          >
            {TICKET_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {priorityLabel(p)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <CreateFooter
        label="Open ticket"
        pendingLabel="Opening…"
        missing={missing}
        pending={open.isPending}
        reasonId={reasonId}
        onSubmit={() =>
          sendOnce(lock, open.mutate, {
            alertId,
            title: title.trim(),
            category: category as TicketCategory,
            priority,
          })
        }
        cancel={{ label: 'Cancel', onCancel }}
      />
    </div>
  );
}
